import React from 'react';
import { createPortal } from 'react-dom';
import { useBookingActions } from '../../lib/useBookingActions';

// Kebab menu attached to any event card / queue row. Renders a small
// popover above the event with the four lifecycle actions gated by the
// booking's current status + whether it's still in the future.
export const StatusMenu = ({ booking, onChanged, size = 'sm', canManage = true, perms }) => {
  const [open, setOpen] = React.useState(false);
  // `perms` gates individual menu entries in the barber-only case
  // (see useBookingActions). shop_admin / master_admin pass no perms
  // and every action stays enabled — matches the server guards.
  const actions = useBookingActions(booking, onChanged, perms);
  const { can, busy, confirmNode } = actions;
  const btnRef = React.useRef(null);
  const menuRef = React.useRef(null);
  // Portal-positioned coords for the dropdown. `null` = not yet measured.
  const [coords, setCoords] = React.useState(null);

  // Recompute the dropdown position from the trigger button's rect.
  // Flips to the LEFT if it would overflow the right edge, and flips
  // UPWARD if it would overflow the bottom of the viewport.
  //
  // We use the menu element's ACTUAL height once it has rendered
  // (falling back to a conservative estimate on the first pass so the
  // menu appears in roughly the right place before the measure step
  // corrects it). This fixes the bug where the menu appeared far above
  // the button because the height estimate was much larger than the
  // real menu.
  const reposition = React.useCallback(() => {
    const btn = btnRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    const MENU_W = 200;
    // Prefer the real measured height once the menu is in the DOM.
    // Before the first paint, fall back to a small default (menus are
    // typically 5 items → ~180px) so the initial position is close.
    const measuredH = menuRef.current?.offsetHeight;
    const MENU_H = measuredH && measuredH > 0 ? measuredH : 180;
    const pad = 8;
    let top = r.bottom + 4;
    let left = r.right - MENU_W;
    if (left < pad) left = pad;
    if (left + MENU_W > window.innerWidth - pad) left = window.innerWidth - MENU_W - pad;
    // Flip up only if the REAL menu height would overflow the bottom.
    if (top + MENU_H > window.innerHeight - pad) {
      top = Math.max(pad, r.top - 4 - MENU_H);
    }
    setCoords({ top, left, width: MENU_W });
  }, []);

  React.useEffect(() => {
    if (!open) return;
    reposition();
    const onScroll = () => reposition();
    const onResize = () => reposition();
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    const onDocDown = (e) => {
      const t = e.target;
      if (menuRef.current?.contains(t)) return;
      if (btnRef.current?.contains(t)) return;
      setOpen(false);
    };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onDocDown, true);
    window.addEventListener('touchstart', onDocDown, true);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onDocDown, true);
      window.removeEventListener('touchstart', onDocDown, true);
    };
  }, [open, reposition]);

  // Second-pass reposition: after the menu has painted we know its real
  // height, so re-run the flip check. `useLayoutEffect` fires before
  // the browser paints so there's no visible jump.
  React.useLayoutEffect(() => {
    if (open && coords && menuRef.current) {
      const realH = menuRef.current.offsetHeight;
      // Only re-run if the current top would push the menu off-screen.
      const overflow = coords.top + realH > window.innerHeight - 8;
      // Or if we flipped up unnecessarily (menu now fits below).
      const overflipped = coords.top < btnRef.current?.getBoundingClientRect().top;
      if (overflow || overflipped) reposition();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, coords?.top]);

  if (!canManage) return null;

  // Wrapper: run an action from the shared hook, closing the menu first
  // so the confirm dialog is the only surface visible.
  const run = (fn) => async () => { setOpen(false); await fn(); };

  const menu = open && coords ? createPortal(
    <div
      ref={menuRef}
      className="ev-kebab-menu"
      role="menu"
      data-testid={`ev-kebab-menu-${booking.id}`}
      style={{ position: 'fixed', top: coords.top, left: coords.left, minWidth: coords.width }}
      onClick={(e) => e.stopPropagation()}
    >
      {can.reschedule && (
        <button role="menuitem" disabled={busy}
          onClick={run(actions.openReschedule)}
          data-testid={`ev-reschedule-${booking.id}`}
        >Reschedule</button>
      )}
      {can.complete && (
        <button role="menuitem" disabled={busy}
          onClick={run(actions.markCompleted)}
          data-testid={`ev-mark-completed-${booking.id}`}
        >Mark completed</button>
      )}
      {can.noShow && (
        <button role="menuitem" disabled={busy}
          onClick={run(actions.markNoShow)}
          data-testid={`ev-mark-noshow-${booking.id}`}
        >Mark no-show</button>
      )}
      {can.cancel && (
        <button role="menuitem" disabled={busy} className="danger"
          onClick={run(actions.cancelBooking)}
          data-testid={`ev-cancel-${booking.id}`}
        >Cancel booking</button>
      )}
      {can.reopen && (
        <button role="menuitem" disabled={busy}
          onClick={run(actions.reopen)}
          data-testid={`ev-reopen-${booking.id}`}
        >Reopen as confirmed</button>
      )}
      {(can.markDeposit || can.markFull || can.unmark) && (
        <div className="ev-kebab-sep" role="separator" />
      )}
      {can.markDeposit && (
        <button role="menuitem" disabled={busy}
          onClick={run(actions.markDepositPaid)}
          data-testid={`ev-mark-deposit-${booking.id}`}
        >Mark deposit paid</button>
      )}
      {can.markFull && (
        <button role="menuitem" disabled={busy}
          onClick={run(actions.markPaidInFull)}
          data-testid={`ev-mark-paid-${booking.id}`}
        >Mark paid in full</button>
      )}
      {can.unmark && (
        <button role="menuitem" disabled={busy}
          onClick={run(actions.undoPayment)}
          data-testid={`ev-unmark-paid-${booking.id}`}
        >Undo payment</button>
      )}
    </div>,
    document.body
  ) : null;

  return (
    <span
      style={{ display: 'inline-block' }}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        ref={btnRef}
        type="button"
        className="ev-kebab"
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        aria-label="Booking actions"
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid={`ev-kebab-${booking.id}`}
        data-size={size}
      >⋯</button>
      {menu}
      {confirmNode}
      {actions.rescheduleNode}
      {actions.paymentSheetNode}
    </span>
  );
};
